/**
 * BIM-style contextual Modify tools for imported drawings, annotations and
 * measurement. Companion of ElementEditTools.ts (element-shape editors).
 */
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from '../BaseTool';
import { BIMElement } from '../../core/types';
import { elementLength, getSelectedElements } from '../../utils/contextualModelUtils';
import i18n from '../../i18n';

/* ==================== MEASURE: Along an Element ==================== */

/**
 * Measure Along an Element: click any element to report the total length of
 * its main geometry (wall centerline, model line, boundary perimeter, …).
 */
export class MeasureAlongElementTool extends BaseTool {
    id = 'CTX_MEASURE_ALONG';
    name = 'Measure Along an Element';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event);
        if (!hit) {
            this.notify(i18n.t('tools.notify.noElementFoundUnderTheCursor'), 'warning');
            return;
        }
        const length = elementLength(hit.element);
        if (length <= 0) {
            this.notify(i18n.t('tools.notify.measureUnsupported', { category: hit.element.category }), 'warning');
            return;
        }
        this.notify(i18n.t('tools.notify.measureAlong', { element: hit.element.name ?? hit.element.category, length: length.toFixed(3) }), 'success');
        this.context.appStore.setActiveTool('select');
    }

    onPointerMove(): void { }
    onPointerUp(): void { }
}

/* ==================== DWG: Query ==================== */

/** Imported-CAD line record: parsed payloads carry `points`, legacy ones
 *  carry start/end pairs. */
interface CadLine {
    start?: [number, number, number];
    end?: [number, number, number];
    points?: [[number, number, number], [number, number, number]];
    layer?: string;
}

/** Normalize a CadLine to absolute world start/end. Parsed payloads store
 *  lines relative to the element origin; legacy start/end payloads are
 *  absolute (mirror of ComponentRenderer's isParsed convention). */
function cadLineEndpoints(ln: CadLine, origin: number[] | undefined): { a: THREE.Vector3; b: THREE.Vector3; layer: string } {
    const off = (ln.points && origin) ? new THREE.Vector3(...origin) : new THREE.Vector3();
    let a: [number, number, number];
    let b: [number, number, number];
    if (ln.points) { [a, b] = ln.points; }
    else { a = ln.start ?? [0, 0, 0]; b = ln.end ?? [0, 0, 0]; }
    return {
        a: new THREE.Vector3(...a).add(off),
        b: new THREE.Vector3(...b).add(off),
        layer: ln.layer ?? '0',
    };
}

/** True when the element is an imported DWG (by type marker, not category). */
export function isImportedCad(el: BIMElement): boolean {
    return el.type === 'Imported CAD' || el.category === 'CAD Links' || el.category === 'Imported Instances';
}

/**
 * Query (Imported CAD): click a CAD line to inspect its layer and endpoints —
 * the BIM "Query" tool subset that works on the imported line records.
 */
export class CadQueryTool extends BaseTool {
    id = 'CTX_QUERY';
    name = 'Query';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event, e => isImportedCad(e));
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickOnAnImportedCadDrawing'), 'warning');
            return;
        }
        const g = (hit.element as any).geometryData;
        const lines: CadLine[] = Array.isArray(g?.lines) ? g.lines : [];
        if (lines.length === 0) {
            this.notify(i18n.t('tools.notify.noLineDataFoundInThe'), 'warning');
            return;
        }

        // Find the individual line closest to the click (in XY, meters)
        let bestDist = 0.5;
        const p = hit.point;
        const origin = g.origin as number[] | undefined;
        let best: { a: THREE.Vector3; b: THREE.Vector3; layer: string } | null = null;
        for (const ln of lines) {
            const ep = cadLineEndpoints(ln, origin);
            const d = distancePointSegment(p, ep.a, ep.b);
            if (d < bestDist) { bestDist = d; best = ep; }
        }
        if (!best) {
            this.notify(i18n.t('tools.notify.noCadLineWithinPickRadius'), 'info');
            return;
        }
        const len = best.a.distanceTo(best.b);
        this.notify(i18n.t('tools.notify.cadLineInfo', { layer: best.layer, length: len.toFixed(3), from: best.a.toArray().map(v => v.toFixed(2)).join(', '), to: best.b.toArray().map(v => v.toFixed(2)).join(', ') }),
            'success'
        );
    }

    onPointerMove(): void { }
    onPointerUp(): void { }
}

function distancePointSegment(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): number {
    const ab = b.clone().sub(a);
    const t = THREE.MathUtils.clamp(p.clone().sub(a).dot(ab) / Math.max(ab.lengthSq(), 1e-9), 0, 1);
    return p.distanceTo(a.clone().add(ab.multiplyScalar(t)));
}

/* ==================== DWG: Explode (Partial) ==================== */

/**
 * Explode (Partial): click a CAD line to pull JUST that line out of the import
 * and convert it into a free-standing Model Line element (BIM's partial
 * explode of one picked sub-element).
 */
export class CadExplodePartialTool extends BaseTool {
    id = 'CTX_EXPLODE_PARTIAL';
    name = 'Explode Partial';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event, e => isImportedCad(e));
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickACadLineToExplode'), 'warning');
            return;
        }
        const g = (hit.element as any).geometryData;
        const lines: CadLine[] = Array.isArray(g?.lines) ? [...g.lines] : [];
        const p = hit.point;
        const origin = g.origin as number[] | undefined;
        let bestIndex = -1;
        let bestDist = 0.5;
        let bestEp: { a: THREE.Vector3; b: THREE.Vector3; layer: string } | null = null;
        lines.forEach((ln, i) => {
            const ep = cadLineEndpoints(ln, origin);
            const d = distancePointSegment(p, ep.a, ep.b);
            if (d < bestDist) { bestDist = d; bestIndex = i; bestEp = ep; }
        });
        if (bestIndex < 0 || !bestEp) {
            this.notify(i18n.t('tools.notify.noCadLineWithinPickRadius2'), 'info');
            return;
        }
        const exploded = bestEp as { a: THREE.Vector3; b: THREE.Vector3; layer: string };
        lines.splice(bestIndex, 1);
        const bim = this.context.bimStore;

        // 1. Remaining line work stays in the import element
        if (lines.length === 0) {
            bim.removeElement(hit.element.id);
        } else {
            bim.updateElement(hit.element.id, {
                geometryData: { ...g, lines },
            } as any);
        }

        // 2. The picked line becomes a world-positioned Model Line
        const start: [number, number, number] = [Number(exploded.a.x.toFixed(4)), Number(exploded.a.y.toFixed(4)), Number(exploded.a.z.toFixed(4))];
        const end: [number, number, number] = [Number(exploded.b.x.toFixed(4)), Number(exploded.b.y.toFixed(4)), Number(exploded.b.z.toFixed(4))];
        bim.addElement({
            id: uuidv4(),
            category: 'ModelLines',
            type: 'Model Line',
            parameters: {
                'Length': exploded.a.distanceTo(exploded.b).toFixed(2) + ' m',
                'Source Layer': exploded.layer,
            },
            constraints: [],
            geometryData: { start, end },
        } as any);
        this.notify(i18n.t('tools.notify.lineExplodedToModelLine'), 'success');
    }

    onPointerMove(): void { }
    onPointerUp(): void { }
}

/* ==================== DIMENSIONS: Edit Witness Lines ==================== */

/**
 * Edit Witness Lines (dimensions): click geometry to add/remove witness
 * references on the selected dimension.
 */
export class EditWitnessLinesTool extends BaseTool {
    id = 'CTX_EDIT_WITNESS';
    name = 'Edit Witness Lines';
    cursor = 'crosshair';

    private dimension: BIMElement | null = null;

    activate(context: any): void {
        super.activate(context);
        const els = getSelectedElements();
        const dim = els.find(e => e.category === 'Dimensions');
        if (!dim) {
            this.exit(i18n.t('tools.notify.selectADimensionFirst'));
            return;
        }
        this.dimension = dim;
        this.notify(i18n.t('tools.notify.clickElementsToAddRemoveWitness'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.dimension || event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event, e => e.id !== this.dimension!.id && e.category !== 'Dimensions');
        if (!hit) {
            this.notify(i18n.t('tools.notify.noReferenceElementUnderTheCursor'), 'warning');
            return;
        }
        const g = (this.dimension as any).geometryData;
        const refs: string[] = Array.isArray(g?.witnessRefs) ? [...g.witnessRefs] : [];
        const idx = refs.indexOf(hit.element.id);
        if (idx >= 0) {
            refs.splice(idx, 1);
            this.notify(i18n.t('tools.notify.witnessRemoved', { element: hit.element.name ?? hit.element.category }), 'info');
        } else {
            refs.push(hit.element.id);
            this.notify(i18n.t('tools.notify.witnessAdded', { element: hit.element.name ?? hit.element.category }), 'info');
        }
        this.context.bimStore.updateElement(this.dimension.id, {
            geometryData: { ...g, witnessRefs: refs },
        } as any);
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    private exit(message?: string): void {
        if (message) this.notify(message, 'warning');
        this.context?.appStore.setActiveTool('select');
    }
}
